import {
  Box,
  IconButton,
  MenuItem,
  Menu,
  styled,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import { ReactComponent as Logo } from "Assets/Images/logo-no-symbol.svg";
import HeaderAvatar from "Components/Header/HeaderAvatar";
import { TOP_BAR_HEIGHT } from "../constants";
import { useUser } from "Hooks/useUser";
import { useCallback, useState } from "react";
import { useSelectedProject } from "Hooks";
import { logout } from "Actions";
import { useDispatch } from "react-redux";
import { AccountCircle, Insights, Logout } from "@mui/icons-material";
import { RouteNames } from "Hooks/routeNames";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { ApiACLRole } from "@incendium/api";
import AiChatModal from "features/ai/components/AiChatModal";
import { useAiContext } from "features/ai/providers/AiProvider";
import AiTextField from "features/ai/components/AiTextField";
import WooCommerceBackfillBanner from "features/dataConnectors/components/WooCommerceBackfillBanner";
import EnvironmentSelector from "./EnvironmentSelector";
import ProjectClientSelector from "./ProjectClientSelector";
import { useLayoutContext } from "Providers/LayoutProvider";
import QuickInsightsDrawer from "features/dashboard/components/QuickInsightsDrawer";
import AccessLevel from "Components/AccessLevel/AccessLevel";

const StyledTopBar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  backgroundColor: theme.palette.primary.dark,
  height: TOP_BAR_HEIGHT,
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
}));

interface ITopBarProps {
  noLinks?: boolean;
}

function TopBar({ noLinks }: ITopBarProps) {
  const { active, setActive } = useAiContext();
  const { quickInsightsOpen, setQuickInsightsOpen } = useLayoutContext();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathByName } = useProtectedRoutes();
  const { wipeLastSelected, selectedProject } = useSelectedProject();
  const { user, hasPermission } = useUser();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (anchorEl) {
        return setAnchorEl(null);
      }
      setAnchorEl(e.currentTarget);
    },
    [anchorEl],
  );

  const doLogout = useCallback(() => {
    wipeLastSelected();
    dispatch(logout());
  }, [dispatch, wipeLastSelected]);

  const onGoHome = useCallback(() => {
    if (hasPermission(ApiACLRole.DOMAIN_ADMIN)) {
      history.push(pathByName(RouteNames.HOME));
    } else if (hasPermission(ApiACLRole.CLIENT_OBSERVER)) {
      history.push(
        pathByName(RouteNames.CLIENT_PAGE, {
          clientId: selectedProject?.clientId,
        }),
      );
    } else if (hasPermission(ApiACLRole.PROJECT_ADMIN)) {
      history.push(
        pathByName(RouteNames.PROJECT_PAGE, {
          clientId: selectedProject?.clientId,
          projectId: selectedProject?.id,
        }),
      );
    } else {
      history.push(
        pathByName(RouteNames.ANALYSE, {
          clientId: selectedProject?.clientId,
          projectId: selectedProject?.id,
        }),
      );
    }
  }, [history, pathByName, hasPermission, selectedProject]);

  const isGetStarted = matchPath(location.pathname, {
    path: "/clients/:clientId/projects/:projectId/get-started",
    exact: true,
    strict: false,
  });

  if (isGetStarted) {
    return null;
  }

  return (
    <StyledTopBar py={1} px={2}>
      <IconButton
        onClick={noLinks ? undefined : onGoHome}
        sx={{ marginBottom: "2px", padding: "8px 16px 8px 3px", flexShrink: 0 }}
      >
        <Logo height={24} width={170} />
      </IconButton>
      <WooCommerceBackfillBanner />
      {!true && !active && selectedProject && (
        <AiTextField
          compact
          onSubmit={() => {
            setActive(true);
          }}
        />
      )}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}
      >
        <AccessLevel role={ApiACLRole.SUPER_ADMIN}>
          <EnvironmentSelector />
        </AccessLevel>
        <ProjectClientSelector />
        {selectedProject && (
          <Tooltip title="Quick Insights">
            <IconButton
              onClick={() => setQuickInsightsOpen((open) => !open)}
              aria-label="Quick Insights"
              sx={{ color: "common.white" }}
            >
              <Insights />
            </IconButton>
          </Tooltip>
        )}
        <IconButton sx={{ padding: 0 }} onClick={handleClick}>
          <HeaderAvatar name={user?.name || ""} />
        </IconButton>
      </Box>
      <Menu
        elevation={0}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
      >
        {!isGetStarted && !noLinks && (
          <MenuItem
            onClick={() => {
              history.push(pathByName(RouteNames.ACCOUNT_ROOT));
            }}
          >
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            Account
          </MenuItem>
        )}
        <MenuItem onClick={doLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      {active && <AiChatModal />}
      <QuickInsightsDrawer
        open={quickInsightsOpen}
        onClose={() => setQuickInsightsOpen(false)}
      />
    </StyledTopBar>
  );
}
export default TopBar;
